/**
 * BrainContext - keeps track of all Notes and their state
 *
 * Provides both async database operations (getNotes, updateNotes, etc.)
 * and synchronous cache access (getCachedNote, getCachedNotes).
 *
 * For reactive subscriptions to note changes, use useNotes() hook.
 * For one-time synchronous reads, use getCachedNote() and getCachedNotes().
 * Use getCachedNote/Notes when you KNOW the note is already in cache (e.g., editing, suggestions).
 */
/* eslint-disable no-param-reassign */
import { storageLogger } from '@annote/core/utils/debug';
import React, { createContext, ReactNode, useContext, useCallback, useMemo, useEffect } from 'react';
import { Note, TypeOfNote, UUID, NoteMap, Source, isUUID } from '@annote/types';
import { createNote } from '@annote/core/utils/notes';
import { useDataContext } from './DataContext';
import { FocusContext } from './FocusContext';
import { annoteIndex } from './annoteIndex';
import { noteCache } from './noteCache';
import { setGetNotesFunction } from './noteCacheHooks';

// Define the interface for the context value
export interface BrainContextType {
  createTakeaway: (fromNote?: Note) => Note;
  updateNotes: (notes: Note[]) => void;
  linkNotes: (fromNote: Note, toNote: Note) => [Note, Note];
  unlinkNotes: (fromNote: Note, toNote: Note) => void;
  searchNotes: (query: string, types?: TypeOfNote[], limit?: number) => Promise<Note[]>;
  getNoteMapByIds: (noteIds: UUID[]) => Promise<NoteMap>;
  getNotes: (noteIds: UUID[]) => Promise<Note[]>;
  getSourceByUrl: (url: string) => Promise<Source | null>;
  deleteNotes: (notes: Note[]) => void;
  customFetch: (input: RequestInfo, init?: RequestInit) => Promise<Response>;
  getCachedNote: (id: UUID) => Note | undefined;
  getCachedNotes: (ids: UUID[]) => Note[];
}

interface BrainProviderProps {
  children: ReactNode;
}

const BrainContext = createContext<BrainContextType | null>(null);

export const BrainProvider: React.FC<BrainProviderProps> = ({ children }) => {
  const { setFocus } = useContext(FocusContext);
  const { annoteDB } = useDataContext();

  if (!annoteDB) throw new Error('annoteDB is not initialized');

  // return a list of notes based on ids, using cache first, then database
  const getNotes = useCallback(
    async (noteIds: UUID[]): Promise<Note[]> => {
      if (!annoteDB) throw new Error('annoteDB is not initialized');

      // double check that the ids are valid
      const cleanIds = noteIds.filter((id) => isUUID(id));

      // Check cache first
      const cachedNotes: Note[] = [];
      const missingIds: string[] = [];

      cleanIds.forEach((id) => {
        if (noteCache.has(id)) {
          const note = noteCache.get(id);
          if (note) {
            cachedNotes.push(note);
          }
        } else {
          missingIds.push(id);
        }
      });

      // Fetch missing notes from database
      let dbNotes: Note[] = [];
      if (missingIds.length > 0) {
        dbNotes = await annoteDB.getNotes(missingIds);
        // Add fetched notes to cache and notify subscribers so UI updates.
        if (dbNotes.length > 0) {
          noteCache.upsertMany(dbNotes); // notify subscribers so hooks receive updates
        }
      }

      // Return all notes in the requested order
      const allNotesMap = new Map<string, Note>();
      [...cachedNotes, ...dbNotes].forEach((note) => {
        allNotesMap.set(note.id, note);
      });

      return cleanIds.map((id) => allNotesMap.get(id)).filter((note): note is Note => !!note);
    },
    [annoteDB],
  );

  // return a map of notes based on ids, including any updated notes
  const getNoteMapByIds = useCallback(
    async (noteIds: string[]): Promise<NoteMap> => {
      const notes = await getNotes(noteIds);
      const noteMap = notes.reduce((nMap, note) => {
        nMap[note.id] = note;
        return nMap;
      }, {} as NoteMap);

      return noteMap;
    },
    [getNotes],
  );

  const updateNotes = useCallback(
    (notes: Note[]): void => {
      if (!annoteDB) throw new Error('annoteDB is not initialized');

      // Update cache first for immediate UI feedback
      noteCache.upsertMany(notes);

      // Then persist to database (filter out suggestions)
      const notesToPersist = notes.filter((n: Note) => !n.suggestionId);
      if (notesToPersist.length > 0) {
        storageLogger.debug('Updating notes:', notesToPersist);
        annoteDB.updateNotes(notesToPersist);
      }
    },
    [annoteDB],
  );

  const deleteNotes = useCallback(
    async (notesToDelete: Note[]): Promise<void> => {
      if (!annoteDB) throw new Error('annoteDB is not initialized');
      await annoteDB.deleteNotes(notesToDelete);

      // Remove deleted notes from cache entirely
      const deletedIds = notesToDelete.map((n) => n.id);
      noteCache.removeMany(deletedIds);
    },
    [annoteDB],
  );

  const getSourceByUrl = useCallback(
    async (url: string): Promise<Source | null> => {
      if (!annoteDB) throw new Error('annoteDB is not initialized');
      const { notes } = await annoteDB.getNotesByUrl(url);
      const source = notes.find((n) => n.type === 'source') as Source | null;
      return source || null;
    },
    [annoteDB],
  );

  const linkNotes = useCallback(
    (fromNote: Note, toNote: Note): [Note, Note] => {
      if (!annoteDB) throw new Error('annoteDB is not initialized');
      if (!fromNote.links.includes(toNote.id)) {
        const updatedFromNote = {
          ...fromNote,
          links: [toNote.id, ...fromNote.links],
        };
        const updatedToNote = {
          ...toNote,
          backlinks: [fromNote.id, ...toNote.backlinks],
        };
        updateNotes([updatedFromNote, updatedToNote]);
        return [updatedFromNote, updatedToNote];
      }
      return [fromNote, toNote];
    },
    [annoteDB],
  );

  const unlinkNotes = useCallback(
    (fromNote: Note, toNote: Note): void => {
      if (!annoteDB) throw new Error('annoteDB is not initialized');
      const newFromLinks = fromNote.links.filter((id) => id !== toNote.id);
      const newToBacklinks = toNote.backlinks.filter((id) => id !== fromNote.id);
      updateNotes([
        { ...fromNote, links: newFromLinks },
        { ...toNote, backlinks: newToBacklinks },
      ]);
    },
    [annoteDB],
  );

  const createTakeaway = (creatingNote?: Note): Note => {
    if (!annoteDB) throw new Error('annoteDB is not initialized');
    const newNote = createNote('', 'takeaway');
    setTimeout(() => setFocus(newNote.id), 0);
    if (!creatingNote) {
      updateNotes([newNote]);
      return newNote;
    }

    // it was created by another note so let's link them
    newNote.createdFromId = creatingNote.id;
    if (creatingNote.type === 'annote') {
      // Takeaways from annotes should start with the same value and links
      newNote.value = creatingNote.value;
      newNote.links = creatingNote.links;
      // add backlinks to this new note
      // TODO - this isn't the most reliable way to enruse these backlinks
      newNote.links.forEach((id) => {
        annoteDB.getNoteById(id).then((n) => {
          if (!n) return;
          const updatedNote = { ...n, backlinks: [newNote.id, ...n.backlinks] };
          updateNotes([updatedNote]);
        });
      });
    }
    // link Notes also saves them
    const [newFromNote] = linkNotes(newNote, creatingNote);
    return newFromNote;
  };

  // Memoize searchNotes separately
  const searchNotes = useCallback(
    (query: string, types?: TypeOfNote[], limit?: number) => {
      return annoteDB.searchNotes(query, types, limit);
    },
    [annoteDB],
  );

  // Synchronous cache access helpers
  // Use these for one-time reads when you KNOW the note is already in cache.
  // Examples: reading a note being edited, reading suggested notes, reading shown notes.
  // For reactive subscriptions, use useNotes() hook instead.
  const getCachedNotes = useCallback((ids: UUID[]): Note[] => {
    return ids.map((id) => noteCache.get(id)).filter((note): note is Note => note !== undefined);
  }, []);

  const getCachedNote = useCallback(
    (id: UUID): Note | undefined => {
      return getCachedNotes([id])[0];
    },
    [getCachedNotes],
  );

  useEffect(() => {
    annoteIndex.initialize(annoteDB);
  }, [annoteDB, annoteIndex]);

  // Register getNotes with the hooks system for auto-fetching
  useEffect(() => {
    setGetNotesFunction(getNotes);
  }, [getNotes]);

  const brain: BrainContextType = useMemo(
    () => ({
      searchNotes, // use memoized version
      getNoteMapByIds,
      getNotes,
      updateNotes,
      customFetch: annoteDB.fetch || window.fetch,
      getSourceByUrl,
      linkNotes,
      unlinkNotes,
      createTakeaway,
      deleteNotes,
      getCachedNote,
      getCachedNotes,
    }),
    [
      searchNotes,
      getNoteMapByIds,
      getNotes,
      updateNotes,
      getSourceByUrl,
      linkNotes,
      unlinkNotes,
      createTakeaway,
      deleteNotes,
      getCachedNote,
      getCachedNotes,
      annoteDB.fetch,
    ],
  );

  return <BrainContext.Provider value={brain}>{children}</BrainContext.Provider>;
};

export const useBrainContext = (): BrainContextType => {
  const context = useContext(BrainContext);
  if (context === null) {
    throw new Error('useBrainContext must be used within a BrainProvider');
  }
  return context;
};
